import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import posthog from 'posthog-js';
import { useParams, Link } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  useDraggable,
  useDroppable,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { Button, Input, InputNumber, Modal, Popconfirm, message, Select, Calendar, Tooltip, TimePicker, Dropdown, AutoComplete } from 'antd';
import type { Dayjs } from 'dayjs';
import { ArrowLeftOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { getClient, updateTrip, pushClientsToBackend } from '../../services/clientsService';
import { publishPlannedTrip } from '../../services/apiService';
import { buildPublishedPayload } from '../../services/publishPayloadBuilder';
import {
  getAllCities,
  getCity,
  getAllActivities,
  getAllMeals,
  getAllHotels,
  getActivitiesByCity,
  getMealsByCity,
  getHotelsByCity,
  createActivity,
  createMeal,
  createHotel,
} from '../../services/listBuilderService';
import dayjs from 'dayjs';
import type { Client, Trip, ItineraryDay, ItineraryItem, ItineraryItemType } from '../../types/client';
import type { ListBuilderActivity, ListBuilderMeal, ListBuilderHotel } from '../../types/listBuilder';
import { itineraryBuilderEn } from '../../content/itineraryBuilder.en';
import { clientCrmEn } from '../../content/clientCrm.en';
import { createEmptyActivity, createEmptyMeal, createEmptyHotel } from '../../types/listBuilder';
import ActivityFormModal from '../../components/ActivityFormModal/ActivityFormModal';
import MealFormModal from '../../components/MealFormModal/MealFormModal';
import HotelFormModal from '../../components/HotelFormModal/HotelFormModal';
import TripDateRangePicker from '../../components/TripDateRangePicker/TripDateRangePicker';
import { formatPublishedPayload } from '../../utils/payloadFormatter';
import { isPostHogConfigured } from '../../lib/posthog';
import styles from './ItineraryBuilder.module.css';

const { TextArea } = Input;

const SHOW_DEBUG_PANEL = false;

const LIST_TYPES: ItineraryItemType[] = ['activity', 'meal', 'accommodation'];
/** Types shown in day card Add Item dropdown and add modal (no transport/other) */
const ADD_ITEM_TYPES: ItineraryItemType[] = ['activity', 'meal', 'accommodation'];

const ACTIVITY_ICON_SRC = '/images/icon-activity-walking.svg';
const ACCOMMODATION_ICON_SRC = '/images/icon-accommodation-bed.svg';

const TYPE_ICONS: Record<ItineraryItemType, React.ReactNode> = {
  activity: <img src={ACTIVITY_ICON_SRC} alt="" />,
  meal: '🍽️',
  accommodation: <img src={ACCOMMODATION_ICON_SRC} alt="" />,
  transport: '🚗',
  note: '📝',
  other: '📌',
};

function getItemCardTypeClass(type: ItineraryItemType): string {
  const map: Record<ItineraryItemType, string> = {
    activity: styles.itemCardActivity,
    meal: styles.itemCardMeal,
    accommodation: styles.itemCardAccommodation,
    transport: '',
    note: '',
    other: '',
  };
  return map[type] ?? '';
}

function getDaysBetween(start: string, end: string): string[] {
  const result: string[] = [];
  let cur = dayjs(start);
  const last = dayjs(end);
  while (cur.isBefore(last, 'day') || cur.isSame(last, 'day')) {
    result.push(cur.format('YYYY-MM-DD'));
    cur = cur.add(1, 'day');
  }
  return result;
}

function formatDayLabel(date: string, dayIndex: number, totalDays: number): string {
  const d = dayjs(date);
  const dateStr = d.format('ddd · MMM D');
  if (totalDays === 1) return dateStr;
  return `${itineraryBuilderEn.dayLabel} ${dayIndex + 1} · ${dateStr}`;
}

const TIME_PARSE_FORMATS = ['h:mm A', 'HH:mm', 'H:mm'];

/** Default time for the next item: after the latest-ending existing item + 30 min. */
function getDefaultTimeForNextItem(items: ItineraryItem[]): string {
  const timedItems = items.filter((i) => i.type !== 'accommodation');
  if (timedItems.length === 0) return '9:00 AM';
  let latestEndMinutes = 0; // minutes since midnight
  for (const item of timedItems) {
    const t = item.time?.trim();
    if (!t) continue;
    const parsed = dayjs(t, TIME_PARSE_FORMATS, true);
    if (!parsed.isValid()) continue;
    const duration = item.durationMinutes ?? 60;
    const endMinutes = parsed.hour() * 60 + parsed.minute() + duration;
    if (endMinutes > latestEndMinutes) latestEndMinutes = endMinutes;
  }
  if (latestEndMinutes === 0) return '9:00 AM';
  const nextStart = latestEndMinutes + 30;
  const h = Math.floor(nextStart / 60) % 24;
  const m = nextStart % 60;
  return dayjs().hour(h).minute(m).second(0).millisecond(0).format('h:mm A');
}

/** True if time is before 5:00 PM. Used for meal duration default (before 5 = 60 min, 5 PM or later = 120 min). */
function isTimeBefore5PM(timeStr: string): boolean {
  const t = dayjs(timeStr.trim(), TIME_PARSE_FORMATS, true);
  if (!t.isValid()) return true;
  const fivePM = t.clone().hour(17).minute(0).second(0).millisecond(0);
  return t.isBefore(fivePM);
}

const DRAGGABLE_PREFIX = 'item::';
const DROPPABLE_PREFIX = 'day-';

function parseDraggableId(id: string): { itemId: string; date: string } | null {
  if (!id.startsWith(DRAGGABLE_PREFIX)) return null;
  const rest = id.slice(DRAGGABLE_PREFIX.length);
  const sep = rest.indexOf('::');
  if (sep === -1) return null;
  return { itemId: rest.slice(0, sep), date: rest.slice(sep + 2) };
}

function parseDroppableId(id: string): string | null {
  if (!id.startsWith(DROPPABLE_PREFIX)) return null;
  return id.slice(DROPPABLE_PREFIX.length);
}

const DroppableDay: React.FC<{
  date: string;
  dayIndex: number;
  totalDays: number;
  items: ItineraryItem[];
  accommodations: ItineraryItem[];
  cityId: string;
  onCityChange: (cityId: string) => void;
  cityOptions: { value: string; label: string }[];
  onAddItem: (date: string, type: ItineraryItemType) => void;
  onEditItem: (item: ItineraryItem, date: string) => void;
  onDeleteItem: (itemId: string, date: string) => void;
}> = ({ date, dayIndex, totalDays, items, accommodations, cityId, onCityChange, cityOptions, onAddItem, onEditItem, onDeleteItem }) => {
  const droppableId = `${DROPPABLE_PREFIX}${date}`;
  const { setNodeRef, isOver } = useDroppable({ id: droppableId });
  const hasAny = items.length > 0 || accommodations.length > 0;
  return (
    <div className={`${styles.dayColumn} ${isOver ? styles.dayColumnOver : ''}`}>
      <div className={styles.dayHeader}>
        <span className={styles.dayLabel}>{formatDayLabel(date, dayIndex, totalDays)}</span>
        <Select
          placeholder={itineraryBuilderEn.cityPlaceholder}
          value={cityId || undefined}
          onChange={(v) => onCityChange(v ?? '')}
          options={cityOptions}
          className={styles.dayCitySelect}
          allowClear
        />
        <Dropdown
          menu={{
            items: ADD_ITEM_TYPES.map((t) => ({
              key: t,
              label: itineraryBuilderEn.itemTypes[t],
              onClick: () => onAddItem(date, t),
            })),
          }}
          trigger={['click']}
        >
          <Button
            type="text"
            size="small"
            icon={<PlusOutlined />}
            className={styles.addItemBtn}
          >
            {itineraryBuilderEn.addItem}
          </Button>
        </Dropdown>
      </div>
      <div ref={setNodeRef} className={styles.dayContent}>
        {!hasAny && (
          <div className={styles.emptyDayPrompt}>{itineraryBuilderEn.emptyDayPrompt}</div>
        )}
        {items.map((item) => (
          <DraggableItem
            key={`${item.id}-${date}`}
            item={item}
            date={date}
            onEdit={() => onEditItem(item, date)}
            onDelete={() => onDeleteItem(item.id, date)}
          />
        ))}
        {accommodations.length > 0 && (
          <>
            <div className={styles.accommodationDivider}>{itineraryBuilderEn.accommodationSection}</div>
            {accommodations.map((item) => {
              const ownerDate = item.accommodationCheckInDate ?? date;
              return (
                <DraggableItem
                  key={`acc-${item.id}-${date}`}
                  item={item}
                  date={date}
                  onEdit={() => onEditItem(item, ownerDate)}
                  onDelete={() => onDeleteItem(item.id, ownerDate)}
                />
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};

const DraggableItem: React.FC<{
  item: ItineraryItem;
  date: string;
  onEdit: () => void;
  onDelete: () => void;
}> = ({ item, date, onEdit, onDelete }) => {
  const isAccommodation = item.type === 'accommodation';
  const draggableId = `${DRAGGABLE_PREFIX}${item.id}::${date}`;
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: draggableId });
  const style: React.CSSProperties | undefined = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined;
  return (
    <div
      ref={setNodeRef}
      {...(isAccommodation ? {} : { ...listeners, ...attributes })}
      className={`${styles.itemCard} ${getItemCardTypeClass(item.type)} ${isDragging ? styles.itemCardDragging : ''} ${isAccommodation ? styles.itemCardNoDrag : ''}`}
      style={style}
    >
      <div className={styles.itemIcon}>{TYPE_ICONS[item.type]}</div>
      <div className={styles.itemBody}>
        {item.type === 'accommodation' && (item.accommodationCheckInDate || item.accommodationCheckOutDate) ? (
          <span className={styles.itemTime}>
            {item.accommodationCheckInDate ? dayjs(item.accommodationCheckInDate).format('MMM D') : '—'}
            {' – '}
            {item.accommodationCheckOutDate ? dayjs(item.accommodationCheckOutDate).format('MMM D') : '—'}
          </span>
        ) : (
          <span className={styles.itemTime}>
            {item.time || '—'}
            {item.durationMinutes != null && item.durationMinutes > 0 && (
              <span className={styles.itemDuration}> · {item.durationMinutes} min</span>
            )}
          </span>
        )}
        <span className={styles.itemTitle}>{item.title || 'Untitled'}</span>
      </div>
      <div className={styles.itemActions} onClick={(e) => e.stopPropagation()}>
        <Button type="text" size="small" icon={<EditOutlined />} onClick={onEdit} />
        <Popconfirm title={`${itineraryBuilderEn.delete}?`} onConfirm={onDelete}>
          <Button type="text" size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      </div>
    </div>
  );
};

const ItineraryBuilder: React.FC = () => {
  const { clientId, tripId } = useParams<{ clientId: string; tripId: string }>();
  const { user, isLoading: authLoading } = useAuth();
  const [client, setClient] = useState<Client | null>(null);
  const [trip, setTrip] = useState<Trip | null>(null);
  const [days, setDays] = useState<ItineraryDay[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<{ item: ItineraryItem; date: string } | null>(null);
  const [formTitle, setFormTitle] = useState('');
  const [formType, setFormType] = useState<ItineraryItemType>('activity');
  const [formTime, setFormTime] = useState('');
  const [formDuration, setFormDuration] = useState<number | ''>(60);
  const [formNotes, setFormNotes] = useState('');
  const [formConfirmationNumber, setFormConfirmationNumber] = useState('');
  const [formListId, setFormListId] = useState<string>('');
  const [addForDate, setAddForDate] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [publishId, setPublishId] = useState('');
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [listActivitiesCityId, setListActivitiesCityId] = useState('');
  const [listMealsCityId, setListMealsCityId] = useState('');
  const [listAccommodationsCityId, setListAccommodationsCityId] = useState('');
  const hasPushedRef = useRef(false);

  // Track initial form values to detect dirtiness on the itinerary item modal
  const modalInitialRef = useRef({
    title: '',
    type: 'activity' as ItineraryItemType,
    time: '',
    duration: 60 as number | '',
    notes: '',
    confirmationNumber: '',
    listId: '',
  });

  // List Builder modal state
  const [activityModalOpen, setActivityModalOpen] = useState(false);
  const [mealModalOpen, setMealModalOpen] = useState(false);
  const [hotelModalOpen, setHotelModalOpen] = useState(false);
  // Counter to trigger re-compute of display lists after adding items
  const [listRefreshKey, setListRefreshKey] = useState(0);
  const [accommodationModalOpen, setAccommodationModalOpen] = useState(false);
  const [accommodationTitle, setAccommodationTitle] = useState('');
  const [accommodationListId, setAccommodationListId] = useState('');
  const [accommodationCheckIn, setAccommodationCheckIn] = useState('');
  const [accommodationCheckOut, setAccommodationCheckOut] = useState('');
  const [accommodationNotes, setAccommodationNotes] = useState('');
  const [accommodationConfirmationNumber, setAccommodationConfirmationNumber] = useState('');
  const [editingAccommodation, setEditingAccommodation] = useState<{ item: ItineraryItem; date: string } | null>(null);

  const cities = useMemo(() => (user?.sub ? getAllCities(user.sub) : []), [user?.sub]);
  const cityOptions = useMemo(
    () => cities.map((c) => ({ value: c.id, label: `${c.name}, ${c.country}` })),
    [cities]
  );

  // When selected day or its city changes, sync the three list city filters to the day card city
  useEffect(() => {
    if (!selectedDate) return;
    const cityId = days.find((d) => d.date === selectedDate)?.cityId ?? '';
    setListActivitiesCityId(cityId);
    setListMealsCityId(cityId);
    setListAccommodationsCityId(cityId);
  }, [selectedDate, days]);

  const displayActivities = useMemo(() => {
    if (!user?.sub) return [];
    if (!listActivitiesCityId) return getAllActivities(user.sub);
    return getActivitiesByCity(user.sub, listActivitiesCityId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.sub, listActivitiesCityId, listRefreshKey]);
  const displayMeals = useMemo(() => {
    if (!user?.sub) return [];
    if (!listMealsCityId) return getAllMeals(user.sub);
    return getMealsByCity(user.sub, listMealsCityId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.sub, listMealsCityId, listRefreshKey]);
  const displayHotels = useMemo(() => {
    if (!user?.sub) return [];
    if (!listAccommodationsCityId) return getAllHotels(user.sub);
    return getHotelsByCity(user.sub, listAccommodationsCityId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.sub, listAccommodationsCityId, listRefreshKey]);

  // Track how many times each List Builder item is used in the itinerary
  const usedListIdCounts = useMemo(() => {
    const counts = new Map<string, number>();
    const inc = (id: string) => counts.set(id, (counts.get(id) ?? 0) + 1);
    for (const day of days) {
      for (const item of day.items) {
        if (item.activityId) inc(item.activityId);
        if (item.mealId) inc(item.mealId);
        if (item.hotelId) inc(item.hotelId);
      }
    }
    return counts;
  }, [days]);

  const loadData = useCallback(() => {
    if (!user?.sub || !clientId) return;
    const c = getClient(user.sub, clientId);
    setClient(c ?? null);
    if (!c || !tripId) {
      setTrip(null);
      setDays([]);
      return;
    }
    const t = c.trips?.find((tr) => tr.id === tripId) ?? null;
    setTrip(t);

    if (!t?.startDate || !t?.endDate) {
      setDays([]);
      return;
    }
    const dateStrings = getDaysBetween(t.startDate, t.endDate);
    const existingByDate = new Map<string, { items: ItineraryItem[]; cityId?: string }>();
    for (const d of t.itineraryDays ?? []) {
      if (d.date) existingByDate.set(d.date, { items: d.items ?? [], cityId: d.cityId });
    }
    const result: ItineraryDay[] = dateStrings.map((date) => {
      const existing = existingByDate.get(date);
      return {
        date,
        items: existing?.items ?? [],
        ...(existing?.cityId ? { cityId: existing.cityId } : {}),
      };
    });
    setDays(result);
    setSelectedDate((prev) => {
      if (!prev || !dateStrings.includes(prev)) return dateStrings[0] ?? null;
      return prev;
    });
  }, [user?.sub, clientId, tripId]);

  useEffect(() => {
    if (authLoading) return;
    loadData();
    // Push existing localStorage data to backend on first load
    if (!hasPushedRef.current && user?.sub) {
      hasPushedRef.current = true;
      pushClientsToBackend(user.sub);
    }
  }, [authLoading, loadData, user?.sub]);

  // Build cityHistories from days' cityIds using list builder city history
  const cityHistoriesForTrip = useMemo(() => {
    if (!user?.sub) return undefined;
    const cityIds = new Set<string>();
    for (const d of days) {
      if (d.cityId) cityIds.add(d.cityId);
    }
    const result: Record<string, { summary: string }> = {};
    for (const cityId of Array.from(cityIds)) {
      const city = getCity(user.sub, cityId);
      result[cityId] = { summary: city?.history?.summary ?? '' };
    }
    return Object.keys(result).length > 0 ? result : undefined;
  }, [days, user?.sub]);

  // Auto-save: persist itinerary and city histories to localStorage (and backend) whenever days change
  const initialLoadRef = useRef(true);
  useEffect(() => {
    // Skip the first render (initial load from storage)
    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      return;
    }
    if (!user?.sub || !clientId || !tripId || days.length === 0) return;
    updateTrip(user.sub, clientId, tripId, { itineraryDays: days, cityHistories: cityHistoriesForTrip });
  }, [days, user?.sub, clientId, tripId, cityHistoriesForTrip]);

  const getItemByDragId = (dragId: string): { item: ItineraryItem; date: string } | null => {
    const parsed = parseDraggableId(dragId);
    if (!parsed) return null;
    for (const d of days) {
      if (d.date !== parsed.date) continue;
      const found = d.items.find((it) => it.id === parsed.itemId);
      if (found) return { item: found, date: d.date };
    }
    return null;
  };

  const getTargetDateFromOverId = (overId: string): string | null => {
    const droppableDate = parseDroppableId(overId);
    if (droppableDate) return droppableDate;
    const draggableParsed = parseDraggableId(overId);
    if (draggableParsed) return draggableParsed.date;
    return null;
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor)
  );

  const handleDragStart = (e: DragStartEvent) => {
    setActiveId(String(e.active.id));
  };

  const handleDragEnd = (e: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = e;
    if (!over) return;
    const activeParsed = parseDraggableId(String(active.id));
    if (!activeParsed) return;
    const { itemId, date: fromDate } = activeParsed;
    const toDate = getTargetDateFromOverId(String(over.id));
    if (!toDate || fromDate === toDate) return;

    setDays((prev) => {
      const next = prev.map((d) => ({ ...d, items: [...d.items] }));
      let moved: ItineraryItem | null = null;
      for (const d of next) {
        const idx = d.items.findIndex((i) => i.id === itemId);
        if (idx !== -1) {
          [moved] = d.items.splice(idx, 1);
          break;
        }
      }
      if (moved) {
        const toDay = next.find((d) => d.date === toDate);
        if (toDay) toDay.items.push(moved);
      }
      return next;
    });
  };

  const openAccommodationModal = (date?: string) => {
    const checkIn = date ?? selectedDate ?? trip?.startDate ?? '';
    const checkOut = trip?.endDate ?? '';
    setAccommodationTitle('');
    setAccommodationListId('');
    setAccommodationCheckIn(checkIn);
    setAccommodationCheckOut(checkOut);
    setAccommodationNotes('');
    setAccommodationConfirmationNumber('');
    setEditingAccommodation(null);
    setAccommodationModalOpen(true);
  };

  // Open add-item modal (from day button or sidebar + button)
  const openAddModal = (date: string, type?: ItineraryItemType, listId?: string, title?: string) => {
    setAddForDate(date);
    setEditingItem(null);
    const t = title ?? '';
    const tp = type ?? 'activity';
    const lid = listId ?? '';
    const dayData = days.find((d) => d.date === date);
    const defaultTime = getDefaultTimeForNextItem(dayData?.items ?? []);
    const defaultDuration = (() => {
      if (tp === 'meal') return isTimeBefore5PM(defaultTime) ? 60 : 120;
      if (tp === 'activity' && lid && user?.sub) {
        const a = getAllActivities(user.sub).find((x) => x.id === lid);
        return a?.suggestedDurationMinutes ?? 60;
      }
      // Hotels don't have duration in list builder; default 60
      return 60;
    })();
    setFormTitle(t);
    setFormType(tp);
    setFormTime(defaultTime);
    setFormDuration(defaultDuration);
    setFormNotes('');
    setFormConfirmationNumber('');
    setFormListId(lid);
    modalInitialRef.current = { title: t, type: tp, time: defaultTime, duration: defaultDuration, notes: '', confirmationNumber: '', listId: lid };
    setModalOpen(true);
  };

  const openEditModal = (item: ItineraryItem, date: string) => {
    if (item.type === 'accommodation') {
      setEditingAccommodation({ item, date });
      setAccommodationTitle(item.title);
      setAccommodationListId(item.hotelId ?? '');
      setAccommodationCheckIn(item.accommodationCheckInDate ?? trip?.startDate ?? '');
      setAccommodationCheckOut(item.accommodationCheckOutDate ?? trip?.endDate ?? '');
      setAccommodationNotes(item.notes ?? '');
      setAccommodationConfirmationNumber(item.confirmationNumber ?? '');
      setAccommodationModalOpen(true);
      return;
    }
    setEditingItem({ item, date });
    setAddForDate(null);
    setFormTitle(item.title);
    setFormType(item.type);
    setFormTime(item.time ?? '');
    setFormDuration(item.durationMinutes ?? '');
    setFormNotes(item.notes ?? '');
    setFormConfirmationNumber(item.confirmationNumber ?? '');
    setFormListId('');
    modalInitialRef.current = {
      title: item.title,
      type: item.type,
      time: item.time ?? '',
      duration: item.durationMinutes ?? '',
      notes: item.notes ?? '',
      confirmationNumber: item.confirmationNumber ?? '',
      listId: '',
    };
    setModalOpen(true);
  };

  const isItemModalDirty = (): boolean => {
    const init = modalInitialRef.current;
    return (
      formTitle !== init.title ||
      formType !== init.type ||
      formTime !== init.time ||
      formDuration !== init.duration ||
      formNotes !== init.notes ||
      formConfirmationNumber !== init.confirmationNumber ||
      formListId !== init.listId
    );
  };

  const handleModalCancel = () => {
    if (isItemModalDirty()) {
      Modal.confirm({
        title: 'Discard changes?',
        content: 'You have unsaved changes. Are you sure you want to close?',
        okText: 'Discard',
        okType: 'danger',
        cancelText: 'Keep editing',
        onOk: () => setModalOpen(false),
      });
    } else {
      setModalOpen(false);
    }
  };

  const getListOptions = (): { id: string; label: string }[] => {
    if (!user?.sub) return [];
    const cityId =
      formType === 'activity'
        ? listActivitiesCityId
        : formType === 'meal'
          ? listMealsCityId
          : formType === 'accommodation'
            ? listAccommodationsCityId
            : '';
    if (formType === 'activity') {
      const list = cityId ? getActivitiesByCity(user.sub, cityId) : getAllActivities(user.sub);
      return list.map((a) => ({ id: a.id, label: a.title }));
    }
    if (formType === 'meal') {
      const list = cityId ? getMealsByCity(user.sub, cityId) : getAllMeals(user.sub);
      return list.map((m) => ({ id: m.id, label: m.name }));
    }
    if (formType === 'accommodation') {
      const list = cityId ? getHotelsByCity(user.sub, cityId) : getAllHotels(user.sub);
      return list.map((h) => ({ id: h.id, label: h.name }));
    }
    return [];
  };

  const handleSaveItem = () => {
    if (editingItem) {
      setDays((prev) =>
        prev.map((d) => {
          if (d.date !== editingItem.date) return d;
          return {
            ...d,
            items: d.items.map((i) =>
              i.id === editingItem.item.id
                ? {
                    ...i,
                    title: formTitle,
                    type: formType,
                    time: formTime || undefined,
                    durationMinutes: typeof formDuration === 'number' ? formDuration : undefined,
                    notes: formNotes || undefined,
                    confirmationNumber: formConfirmationNumber.trim() || undefined,
                  }
                : i
            ),
          };
        })
      );
    } else if (addForDate && user?.sub) {
      // Adding from list
      if (LIST_TYPES.includes(formType) && !formListId) {
        message.warning(itineraryBuilderEn.selectListItem);
        return;
      }
      let newItem: ItineraryItem;
      if (formListId && LIST_TYPES.includes(formType)) {
        if (formType === 'activity') {
          const a = getAllActivities(user.sub).find((x) => x.id === formListId);
          newItem = {
            id: crypto.randomUUID(),
            title: a?.title ?? formTitle,
            type: 'activity',
            time: formTime || undefined,
            durationMinutes: typeof formDuration === 'number' ? formDuration : undefined,
            notes: formNotes || undefined,
            confirmationNumber: formConfirmationNumber.trim() || undefined,
            activityId: a?.id,
          };
        } else if (formType === 'meal') {
          const m = getAllMeals(user.sub).find((x) => x.id === formListId);
          newItem = {
            id: crypto.randomUUID(),
            title: m?.name ?? formTitle,
            type: 'meal',
            time: formTime || undefined,
            durationMinutes: typeof formDuration === 'number' ? formDuration : undefined,
            notes: formNotes || undefined,
            confirmationNumber: formConfirmationNumber.trim() || undefined,
            mealId: m?.id,
          };
        } else if (formType === 'accommodation') {
          const h = getAllHotels(user.sub).find((x) => x.id === formListId);
          newItem = {
            id: crypto.randomUUID(),
            title: h?.name ?? formTitle,
            type: 'accommodation',
            time: formTime || undefined,
            durationMinutes: typeof formDuration === 'number' ? formDuration : undefined,
            notes: formNotes || undefined,
            confirmationNumber: formConfirmationNumber.trim() || undefined,
            hotelId: h?.id,
          };
        } else {
          newItem = {
            id: crypto.randomUUID(),
            title: formTitle,
            type: formType,
            time: formTime || undefined,
            durationMinutes: typeof formDuration === 'number' ? formDuration : undefined,
            notes: formNotes || undefined,
            confirmationNumber: formConfirmationNumber.trim() || undefined,
          };
        }
      } else {
        newItem = {
          id: crypto.randomUUID(),
          title: formTitle,
          type: formType,
          time: formTime || undefined,
          durationMinutes: typeof formDuration === 'number' ? formDuration : undefined,
          notes: formNotes || undefined,
          confirmationNumber: formConfirmationNumber.trim() || undefined,
        };
      }
      setDays((prev) =>
        prev.map((d) =>
          d.date === addForDate ? { ...d, items: [...d.items, newItem] } : d
        )
      );
    }
    setModalOpen(false);
  };

  const handleDeleteItem = (itemId: string, date: string) => {
    setDays((prev) =>
      prev.map((d) =>
        d.date === date ? { ...d, items: d.items.filter((i) => i.id !== itemId) } : d
      )
    );
  };

  const clientEmailOptions = useMemo(() => {
    if (!client) return [];
    const emails: string[] = [];
    if (client.personalEmail?.trim()) emails.push(client.personalEmail.trim());
    if (client.workEmail?.trim() && !emails.includes(client.workEmail.trim())) {
      emails.push(client.workEmail.trim());
    }
    return emails.map((e) => ({ value: e, label: e }));
  }, [client]);

  const handleOpenPublishModal = () => {
    const defaultId = client?.personalEmail?.trim() || client?.workEmail?.trim() || '';
    setPublishId(defaultId);
    setPublishModalOpen(true);
  };

  const handlePublishConfirm = async () => {
    if (!user?.sub || !clientId || !tripId || !trip) return;
    const id = publishId.trim().toLowerCase();
    if (!id) {
      message.error(itineraryBuilderEn.publishIdRequired);
      return;
    }
    // Save locally first (include city histories for cities on the trip)
    const updated = updateTrip(user.sub, clientId, tripId, {
      itineraryDays: days,
      cityHistories: cityHistoriesForTrip,
    });
    if (!updated) return;

    const listBuilderData = {
      cities: getAllCities(user.sub),
      activities: getAllActivities(user.sub),
      meals: getAllMeals(user.sub),
      hotels: getAllHotels(user.sub),
    };
    const payload = buildPublishedPayload({ ...updated }, listBuilderData);

    setPublishing(true);
    const result = await publishPlannedTrip(id, payload);
    setPublishing(false);
    if (result.success) {
      if (isPostHogConfigured) {
        posthog.capture('itinerary_published', {
          day_count: days.length,
          item_count: days.reduce((count, day) => count + day.items.length, 0),
        });
      }
      message.success(itineraryBuilderEn.publishSuccess);
      setPublishModalOpen(false);
    } else {
      message.error(result.error || itineraryBuilderEn.publishError);
    }
  };

  // Handler for sidebar "+" buttons
  const handleAddSidebarItem = (type: ItineraryItemType, listId: string, title: string) => {
    if (!selectedDate) return;
    if (type === 'accommodation') {
      setAccommodationTitle(title ?? '');
      setAccommodationListId(listId ?? '');
      setAccommodationCheckIn(selectedDate);
      setAccommodationCheckOut(trip?.endDate ?? '');
      setAccommodationNotes('');
      setEditingAccommodation(null);
      setAccommodationModalOpen(true);
      return;
    }
    openAddModal(selectedDate, type, listId, title);
  };

  const getAccommodationsForDay = useCallback((dayDate: string): ItineraryItem[] => {
    const result: ItineraryItem[] = [];
    for (const d of days) {
      for (const item of d.items) {
        if (item.type !== 'accommodation') continue;
        if (item.accommodationCheckInDate && item.accommodationCheckOutDate) {
          const checkIn = item.accommodationCheckInDate;
          const checkOut = item.accommodationCheckOutDate;
          const isSameDay = checkIn === checkOut;
          const inRange = isSameDay
            ? dayDate === checkIn
            : dayDate >= checkIn && dayDate < checkOut;
          if (inRange) result.push(item);
        } else {
          if (d.date === dayDate) result.push(item);
        }
      }
    }
    return result;
  }, [days]);

  const handleSaveAccommodation = () => {
    const checkIn = accommodationCheckIn.trim();
    const checkOut = accommodationCheckOut.trim();
    if (!checkIn || !checkOut) {
      message.warning(itineraryBuilderEn.accommodationDatesRequired);
      return;
    }
    if (dayjs(checkOut).isBefore(dayjs(checkIn), 'day')) {
      message.error(itineraryBuilderEn.accommodationDateInvalid);
      return;
    }
    if (editingAccommodation) {
      setDays((prev) =>
        prev.map((d) => {
          if (d.date !== editingAccommodation.date) return d;
          return {
            ...d,
            items: d.items.map((i) =>
              i.id === editingAccommodation.item.id
                ? {
                  ...i,
                  title: accommodationTitle,
                  notes: accommodationNotes || undefined,
                  confirmationNumber: accommodationConfirmationNumber.trim() || undefined,
                  hotelId: accommodationListId || undefined,
                  accommodationCheckInDate: checkIn,
                  accommodationCheckOutDate: checkOut,
                }
                : i
            ),
          };
        })
      );
    } else {
      if (!accommodationListId && !accommodationTitle.trim()) {
        message.warning(itineraryBuilderEn.selectListItem);
        return;
      }
      const hotel = user?.sub ? getAllHotels(user.sub).find((x) => x.id === accommodationListId) : null;
      const newItem: ItineraryItem = {
        id: crypto.randomUUID(),
        title: hotel?.name ?? accommodationTitle.trim(),
        type: 'accommodation',
        notes: accommodationNotes || undefined,
        confirmationNumber: accommodationConfirmationNumber.trim() || undefined,
        hotelId: accommodationListId || undefined,
        accommodationCheckInDate: checkIn,
        accommodationCheckOutDate: checkOut,
      };
      const targetDay = days.find((d) => d.date === checkIn);
      if (!targetDay) {
        message.error('Check-in date must be within the trip dates.');
        return;
      }
      setDays((prev) =>
        prev.map((d) => (d.date === checkIn ? { ...d, items: [...d.items, newItem] } : d))
      );
    }
    setAccommodationModalOpen(false);
    setEditingAccommodation(null);
  };

  // Handlers for List Builder modals (creating new items)
  const handleSaveNewActivity = (values: Partial<ListBuilderActivity> & { cityId: string }) => {
    if (!user?.sub) return;
    createActivity(user.sub, { ...createEmptyActivity(), ...values });
    setListRefreshKey((k) => k + 1);
  };
  const handleSaveNewMeal = (values: Partial<ListBuilderMeal> & { cityId: string }) => {
    if (!user?.sub) return;
    createMeal(user.sub, { ...createEmptyMeal(), ...values });
    setListRefreshKey((k) => k + 1);
  };
  const handleSaveNewHotel = (values: Partial<ListBuilderHotel> & { cityId: string }) => {
    if (!user?.sub) return;
    createHotel(user.sub, { ...createEmptyHotel(), ...values });
    setListRefreshKey((k) => k + 1);
  };

  // Default city ID for List Builder modals
  const defaultCityId = selectedDate
    ? (days.find((d) => d.date === selectedDate)?.cityId ?? '')
    : '';

  if (authLoading || !user) {
    return (
      <div className={styles.root}>
        <div className={styles.loading}>Loading…</div>
      </div>
    );
  }

  if (!clientId || !tripId || !client) {
    return (
      <div className={styles.root}>
        <div className={styles.error}>Client or trip not found.</div>
        <Link to="/clients">Back to clients</Link>
      </div>
    );
  }

  if (!trip) {
    return (
      <div className={styles.root}>
        <div className={styles.error}>Trip not found.</div>
        <Link to={`/clients/${clientId}`}>Back to client</Link>
      </div>
    );
  }

  if (!trip.startDate || !trip.endDate) {
    return (
      <div className={styles.root}>
        <Link to={`/clients/${clientId}`} className={styles.backLink}>
          <ArrowLeftOutlined /> {itineraryBuilderEn.backToClient}
        </Link>
        <h1>{itineraryBuilderEn.pageTitle}</h1>
        <p className={styles.noDates}>{itineraryBuilderEn.noDates}</p>
      </div>
    );
  }

  const clientName = [client.firstName, client.lastName].filter(Boolean).join(' ') || 'Client';
  const tripTypeLabel = trip.tripType ? clientCrmEn.trip.tripTypeOptions[trip.tripType] : null;
  const tripDateRange = trip.startDate && trip.endDate
    ? `${dayjs(trip.startDate).format('MMM D')} - ${dayjs(trip.endDate).format('MMM D, YYYY')}`
    : '';

  return (
    <div className={styles.root}>
      <header className={styles.headerWrap}>
        <Link to={`/clients/${clientId}`} className={styles.backLink}>
          <ArrowLeftOutlined /> {itineraryBuilderEn.backToClient}
        </Link>
        <div className={styles.headerRow}>
          <h1 className={styles.title}>
            {clientName}
            {tripTypeLabel && (
              <span className={styles.titleMeta}>: {tripTypeLabel}</span>
            )}
            {tripDateRange && (
              <span className={styles.titleMeta}> {tripDateRange}</span>
            )}
          </h1>
          <Link to="/list-builder" className={styles.listBuilderLink}>
            {itineraryBuilderEn.listBuilderLink}
          </Link>
          <Button type="primary" size="large" onClick={handleOpenPublishModal} className={styles.publishBtn}>
            {itineraryBuilderEn.publish}
          </Button>
        </div>
      </header>

      <Modal
        title={itineraryBuilderEn.publishModalTitle}
        open={publishModalOpen}
        onCancel={() => setPublishModalOpen(false)}
        onOk={handlePublishConfirm}
        okText={itineraryBuilderEn.publish}
        cancelText={itineraryBuilderEn.cancel}
        confirmLoading={publishing}
        destroyOnClose
      >
        <div className={styles.publishModalBody}>
          <label htmlFor="publish-id-input" className={styles.publishIdLabel}>
            {itineraryBuilderEn.publishIdLabel}
          </label>
          <AutoComplete
            id="publish-id-input"
            value={publishId}
            onChange={(v) => setPublishId(v ?? '')}
            options={clientEmailOptions}
            placeholder={itineraryBuilderEn.publishIdPlaceholder}
            aria-label={itineraryBuilderEn.publishIdLabel}
            style={{ width: '100%' }}
          />
        </div>
      </Modal>

      <Modal
        title={editingAccommodation ? itineraryBuilderEn.editAccommodationTitle : itineraryBuilderEn.accommodationModalTitle}
        open={accommodationModalOpen}
        onCancel={() => { setAccommodationModalOpen(false); setEditingAccommodation(null); }}
        onOk={handleSaveAccommodation}
        okText={itineraryBuilderEn.save}
        cancelText={itineraryBuilderEn.cancel}
        destroyOnClose
      >
        <div className={styles.form}>
          <label>{itineraryBuilderEn.itemType}</label>
          <select
            value="accommodation"
            onChange={(e) => {
              const v = e.target.value as ItineraryItemType;
              if (v !== 'accommodation') {
                const dateForAdd = accommodationCheckIn || (editingAccommodation?.date) || selectedDate || undefined;
                setAccommodationModalOpen(false);
                setEditingAccommodation(null);
                if (dateForAdd) openAddModal(dateForAdd, v);
              }
            }}
            className={styles.select}
          >
            {ADD_ITEM_TYPES.map((k) => (
              <option key={k} value={k}>{itineraryBuilderEn.itemTypes[k]}</option>
            ))}
          </select>
          <label>{itineraryBuilderEn.itemTitle}</label>
          <Input
            value={accommodationTitle}
            onChange={(e) => setAccommodationTitle(e.target.value)}
            placeholder={itineraryBuilderEn.itemTitlePlaceholder}
          />
          <label>{itineraryBuilderEn.accommodations}</label>
          <Select
            placeholder={itineraryBuilderEn.sourceFromList}
            value={accommodationListId || undefined}
            onChange={(v) => {
              setAccommodationListId(v ?? '');
              const h = user?.sub ? getAllHotels(user.sub).find((x) => x.id === v) : null;
              if (h) setAccommodationTitle(h.name);
            }}
            options={displayHotels.map((h) => ({ value: h.id, label: h.name }))}
            allowClear
            className={styles.select}
          />
          <label>{itineraryBuilderEn.checkInDate} – {itineraryBuilderEn.checkOutDate}</label>
          <TripDateRangePicker
            value={{
              startDate: accommodationCheckIn || null,
              endDate: accommodationCheckOut || null,
            }}
            onChange={(v) => {
              setAccommodationCheckIn(v.startDate ?? '');
              setAccommodationCheckOut(v.endDate ?? '');
            }}
            startDatePlaceholder={itineraryBuilderEn.checkInDate}
            endDatePlaceholder={itineraryBuilderEn.checkOutDate}
            disabledDate={
              trip?.startDate && trip?.endDate
                ? (d) =>
                    d.isBefore(dayjs(trip.startDate), 'day') || d.isAfter(dayjs(trip.endDate), 'day')
                : undefined
            }
          />
          <label>{itineraryBuilderEn.notes}</label>
          <TextArea
            value={accommodationNotes}
            onChange={(e) => setAccommodationNotes(e.target.value)}
            placeholder={itineraryBuilderEn.notesPlaceholder}
            rows={2}
          />
          <label>{itineraryBuilderEn.confirmationNumber}</label>
          <Input
            placeholder={itineraryBuilderEn.confirmationNumberPlaceholder}
            value={accommodationConfirmationNumber}
            onChange={(e) => setAccommodationConfirmationNumber(e.target.value)}
          />
        </div>
      </Modal>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className={styles.mainLayout}>
          <div className={styles.leftColumn}>
            <div className={styles.calendarWrap}>
              <Calendar
                fullscreen={false}
                value={selectedDate ? dayjs(selectedDate) : undefined}
                onSelect={(d: Dayjs) => setSelectedDate(d.format('YYYY-MM-DD'))}
                disabledDate={(d) => {
                  if (!trip?.startDate || !trip?.endDate) return true;
                  const start = dayjs(trip.startDate);
                  const end = dayjs(trip.endDate);
                  return d.isBefore(start, 'day') || d.isAfter(end, 'day');
                }}
                className={styles.calendar}
              />
            </div>
            {selectedDate && (() => {
              const dayData = days.find((d) => d.date === selectedDate);
              const idx = days.findIndex((d) => d.date === selectedDate);
              if (!dayData || idx === -1) return null;
              const timedItems = dayData.items.filter((i) => i.type !== 'accommodation');
              const accommodationsForDay = getAccommodationsForDay(dayData.date);
              return (
                <DroppableDay
                  key={dayData.date}
                  date={dayData.date}
                  dayIndex={idx}
                  totalDays={days.length}
                  items={timedItems}
                  accommodations={accommodationsForDay}
                  cityId={dayData.cityId ?? ''}
                  onCityChange={(cityId) =>
                    setDays((prev) =>
                      prev.map((d) =>
                        d.date === dayData.date ? { ...d, cityId: cityId || undefined } : d
                      )
                    )
                  }
                  cityOptions={cityOptions}
                  onAddItem={(date, type) => {
                    if (type === 'accommodation') openAccommodationModal(date);
                    else openAddModal(date, type);
                  }}
                  onEditItem={openEditModal}
                  onDeleteItem={handleDeleteItem}
                />
              );
            })()}
          </div>
          <div className={styles.rightColumn}>
            <div className={styles.listBuilderColumns}>
              {/* Activities */}
              <div className={styles.listBuilderSection}>
                <div className={styles.listBuilderSectionHeader}>
                  <h3 className={styles.listBuilderSectionTitle}>{itineraryBuilderEn.activities}</h3>
                  <Button type="text" size="small" icon={<PlusOutlined />} onClick={() => setActivityModalOpen(true)}>
                    {itineraryBuilderEn.addNew}
                  </Button>
                </div>
                <Select
                  placeholder={itineraryBuilderEn.cityPlaceholder}
                  value={listActivitiesCityId || undefined}
                  onChange={(v) => setListActivitiesCityId(v ?? '')}
                  options={cityOptions}
                  className={styles.listCitySelect}
                  allowClear
                />
                <ul className={styles.listBuilderList}>
                  {displayActivities.map((a) => {
                    const count = usedListIdCounts.get(a.id) ?? 0;
                    return (
                      <li key={a.id} className={styles.listBuilderItemRow}>
                        <Tooltip title={!selectedDate ? itineraryBuilderEn.selectDayFirst : undefined}>
                          <Button
                            type="text"
                            size="small"
                            icon={<PlusOutlined />}
                            className={styles.listBuilderItemAddBtn}
                            disabled={!selectedDate}
                            onClick={() => handleAddSidebarItem('activity', a.id, a.title)}
                          />
                        </Tooltip>
                        <span className={styles.listBuilderItemName}>{a.title}</span>
                        {count > 0 && (
                          <span className={styles.listBuilderItemCheckmarks}>
                            <span className={styles.overlap}>
                              {Array.from({ length: Math.min(count, 3) }, (_, i) => (
                                <span key={i}>✓</span>
                              ))}
                            </span>
                          </span>
                        )}
                      </li>
                    );
                  })}
                  {displayActivities.length === 0 && (
                    <li className={styles.listBuilderEmpty}>{itineraryBuilderEn.noListItemsShort}</li>
                  )}
                </ul>
              </div>

              {/* Meals */}
              <div className={styles.listBuilderSection}>
                <div className={styles.listBuilderSectionHeader}>
                  <h3 className={styles.listBuilderSectionTitle}>{itineraryBuilderEn.meals}</h3>
                  <Button type="text" size="small" icon={<PlusOutlined />} onClick={() => setMealModalOpen(true)}>
                    {itineraryBuilderEn.addNew}
                  </Button>
                </div>
                <Select
                  placeholder={itineraryBuilderEn.cityPlaceholder}
                  value={listMealsCityId || undefined}
                  onChange={(v) => setListMealsCityId(v ?? '')}
                  options={cityOptions}
                  className={styles.listCitySelect}
                  allowClear
                />
                <ul className={styles.listBuilderList}>
                  {displayMeals.map((m) => {
                    const count = usedListIdCounts.get(m.id) ?? 0;
                    return (
                      <li key={m.id} className={styles.listBuilderItemRow}>
                        <Tooltip title={!selectedDate ? itineraryBuilderEn.selectDayFirst : undefined}>
                          <Button
                            type="text"
                            size="small"
                            icon={<PlusOutlined />}
                            className={styles.listBuilderItemAddBtn}
                            disabled={!selectedDate}
                            onClick={() => handleAddSidebarItem('meal', m.id, m.name)}
                          />
                        </Tooltip>
                        <span className={styles.listBuilderItemName}>{m.name}</span>
                        {count > 0 && (
                          <span className={styles.listBuilderItemCheckmarks}>
                            <span className={styles.overlap}>
                              {Array.from({ length: Math.min(count, 3) }, (_, i) => (
                                <span key={i}>✓</span>
                              ))}
                            </span>
                          </span>
                        )}
                      </li>
                    );
                  })}
                  {displayMeals.length === 0 && (
                    <li className={styles.listBuilderEmpty}>{itineraryBuilderEn.noListItemsShort}</li>
                  )}
                </ul>
              </div>

              {/* Accommodations */}
              <div className={styles.listBuilderSection}>
                <div className={styles.listBuilderSectionHeader}>
                  <h3 className={styles.listBuilderSectionTitle}>{itineraryBuilderEn.accommodations}</h3>
                  <Button type="text" size="small" icon={<PlusOutlined />} onClick={() => setHotelModalOpen(true)}>
                    {itineraryBuilderEn.addNew}
                  </Button>
                </div>
                <Select
                  placeholder={itineraryBuilderEn.cityPlaceholder}
                  value={listAccommodationsCityId || undefined}
                  onChange={(v) => setListAccommodationsCityId(v ?? '')}
                  options={cityOptions}
                  className={styles.listCitySelect}
                  allowClear
                />
                <ul className={styles.listBuilderList}>
                  {displayHotels.map((h) => {
                    const count = usedListIdCounts.get(h.id) ?? 0;
                    return (
                      <li key={h.id} className={styles.listBuilderItemRow}>
                        <Tooltip title={!selectedDate ? itineraryBuilderEn.selectDayFirst : undefined}>
                          <Button
                            type="text"
                            size="small"
                            icon={<PlusOutlined />}
                            className={styles.listBuilderItemAddBtn}
                            disabled={!selectedDate}
                            onClick={() => handleAddSidebarItem('accommodation', h.id, h.name)}
                          />
                        </Tooltip>
                        <span className={styles.listBuilderItemName}>{h.name}</span>
                        {count > 0 && (
                          <span className={styles.listBuilderItemCheckmarks}>
                            <span className={styles.overlap}>
                              {Array.from({ length: Math.min(count, 3) }, (_, i) => (
                                <span key={i}>✓</span>
                              ))}
                            </span>
                          </span>
                        )}
                      </li>
                    );
                  })}
                  {displayHotels.length === 0 && (
                    <li className={styles.listBuilderEmpty}>{itineraryBuilderEn.noListItemsShort}</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* TEMPORARY: Debug label - Payload Preview (toggle SHOW_DEBUG_PANEL to enable) */}
        {SHOW_DEBUG_PANEL && (() => {
          const listBuilderData = user?.sub ? {
            cities: getAllCities(user.sub),
            activities: getAllActivities(user.sub),
            meals: getAllMeals(user.sub),
            hotels: getAllHotels(user.sub),
          } : { cities: [], activities: [], meals: [], hotels: [] };
          const tripWithDays = { ...trip, itineraryDays: days, cityHistories: cityHistoriesForTrip };
          const payload = buildPublishedPayload(tripWithDays, listBuilderData);
          const formatted = formatPublishedPayload(payload);
          const rawJson = JSON.stringify(payload, null, 2);
          return (
            <div style={{ padding: '16px', background: '#f5f5f5', borderRadius: '8px', margin: '16px 0', whiteSpace: 'pre', fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace', fontSize: '12px', maxHeight: '1200px', overflow: 'auto' }}>
              {formatted}
              {'\n\n'}
              <span style={{ color: '#666', fontWeight: 'bold' }}>{'═'.repeat(60)}</span>
              {'\n'}
              <span style={{ color: '#666', fontWeight: 'bold' }}>RAW JSON PAYLOAD</span>
              {'\n'}
              <span style={{ color: '#666', fontWeight: 'bold' }}>{'═'.repeat(60)}</span>
              {'\n\n'}
              {rawJson}
            </div>
          );
        })()}

        <DragOverlay>
          {activeId ? (() => {
            const found = getItemByDragId(activeId);
            if (!found) return null;
            const { item } = found;
            return (
              <div className={`${styles.itemCard} ${getItemCardTypeClass(item.type)} ${styles.itemCardDragging}`}>
                <div className={styles.itemIcon}>{TYPE_ICONS[item.type]}</div>
                <div className={styles.itemBody}>
                  <span className={styles.itemTime}>
                    {item.time || '—'}
                    {item.durationMinutes != null && item.durationMinutes > 0 && (
                      <span className={styles.itemDuration}> · {item.durationMinutes} min</span>
                    )}
                  </span>
                  <span className={styles.itemTitle}>{item.title || 'Untitled'}</span>
                </div>
              </div>
            );
          })() : null}
        </DragOverlay>
      </DndContext>

      {/* Add/edit itinerary item modal */}
      <Modal
        title={editingItem ? 'Edit item' : itineraryBuilderEn.newItem}
        open={modalOpen}
        onOk={handleSaveItem}
        onCancel={handleModalCancel}
        okText={itineraryBuilderEn.save}
        cancelText={itineraryBuilderEn.cancel}
      >
        <div className={styles.form}>
          <label>{itineraryBuilderEn.itemType}</label>
          <select
            value={formType}
            onChange={(e) => {
              const v = e.target.value as ItineraryItemType;
              if (v === 'accommodation') {
                setModalOpen(false);
                openAccommodationModal(addForDate ?? undefined);
                return;
              }
              setFormType(v);
              setFormListId('');
            }}
            className={styles.select}
          >
            {ADD_ITEM_TYPES.map((k) => (
              <option key={k} value={k}>{itineraryBuilderEn.itemTypes[k]}</option>
            ))}
          </select>

          {!editingItem && LIST_TYPES.includes(formType) && (
            <>
              <label>
                {formType === 'activity'
                  ? itineraryBuilderEn.itemTypes.activity
                  : formType === 'meal'
                    ? itineraryBuilderEn.itemTypes.meal
                    : itineraryBuilderEn.itemTypes.accommodation}
              </label>
              {getListOptions().length === 0 ? (
                <p className={styles.noListMessage}>
                  {itineraryBuilderEn.noListItems}{' '}
                  <Link to="/list-builder">{itineraryBuilderEn.listBuilderLink}</Link>
                </p>
              ) : (
                <Select
                  placeholder={itineraryBuilderEn.sourceFromList}
                  value={formListId || undefined}
                  onChange={(v) => {
                    setFormListId(v ?? '');
                    if (user?.sub) {
                      if (formType === 'activity') {
                        const a = getAllActivities(user.sub).find((x) => x.id === v);
                        if (a) setFormTitle(a.title);
                      } else if (formType === 'meal') {
                        const m = getAllMeals(user.sub).find((x) => x.id === v);
                        if (m) setFormTitle(m.name);
                      } else if (formType === 'accommodation') {
                        const h = getAllHotels(user.sub).find((x) => x.id === v);
                        if (h) setFormTitle(h.name);
                      }
                    }
                  }}
                  style={{ width: '100%' }}
                  options={getListOptions().map((o) => ({ value: o.id, label: o.label }))}
                  showSearch
                  filterOption={(input, opt) =>
                    (opt?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                  }
                />
              )}
            </>
          )}

          {(editingItem || !LIST_TYPES.includes(formType)) && (
            <>
              <label>{itineraryBuilderEn.itemTitle}</label>
              <Input
                placeholder={itineraryBuilderEn.itemTitlePlaceholder}
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
              />
            </>
          )}

          <label>{itineraryBuilderEn.time}</label>
          <TimePicker
            format="h:mm A"
            use12Hours
            value={
              formTime && dayjs(formTime, ['h:mm A', 'HH:mm', 'H:mm'], true).isValid()
                ? dayjs(formTime, ['h:mm A', 'HH:mm', 'H:mm'], true)
                : null
            }
            onChange={(_, timeStr) => setFormTime(typeof timeStr === 'string' ? timeStr : '')}
            placeholder={itineraryBuilderEn.timePlaceholder}
            style={{ width: '100%' }}
            minuteStep={15}
          />
          <label>{itineraryBuilderEn.duration}</label>
          <InputNumber
            min={0}
            step={15}
            placeholder={itineraryBuilderEn.durationPlaceholder}
            value={formDuration === '' ? undefined : formDuration}
            onChange={(v) => setFormDuration(v ?? '')}
            style={{ width: '100%' }}
          />
          <label>{itineraryBuilderEn.notes}</label>
          <TextArea
            rows={2}
            placeholder={itineraryBuilderEn.notesPlaceholder}
            value={formNotes}
            onChange={(e) => setFormNotes(e.target.value)}
          />
          <label>{itineraryBuilderEn.confirmationNumber}</label>
          <Input
            placeholder={itineraryBuilderEn.confirmationNumberPlaceholder}
            value={formConfirmationNumber}
            onChange={(e) => setFormConfirmationNumber(e.target.value)}
          />
        </div>
      </Modal>

      {/* List Builder modals for creating new items */}
      <ActivityFormModal
        open={activityModalOpen}
        onClose={() => setActivityModalOpen(false)}
        onSave={handleSaveNewActivity}
        cities={cities}
        cityId={defaultCityId}
      />
      <MealFormModal
        open={mealModalOpen}
        onClose={() => setMealModalOpen(false)}
        onSave={handleSaveNewMeal}
        cities={cities}
        cityId={defaultCityId}
      />
      <HotelFormModal
        open={hotelModalOpen}
        onClose={() => setHotelModalOpen(false)}
        onSave={handleSaveNewHotel}
        cities={cities}
        cityId={defaultCityId}
      />
    </div>
  );
};

export default ItineraryBuilder;
